/**
 * StackFlowGraphic — sdílená vizualizace stacku (orchestrátor / Codable / AppHost).
 * Řetěz internet → proxy → frontend → backend + karty služeb se stavem.
 */
import {
  Archive,
  CheckCircle2,
  Cloud,
  Database,
  Globe,
  Loader2,
  Server,
  XCircle,
} from 'lucide-react';
import { cn } from '../lib/utils';

export type StackServiceStatus = 'pending' | 'running' | 'done' | 'error';

export interface StackService {
  name: string;
  module: string;
  status?: StackServiceStatus;
  subtitle?: string;
  node?: string | null;
}

const ICONS: Record<string, typeof Database> = {
  dbhost: Database,
  apphost: Server,
  supabase: Database,
  npm: Globe,
  dns: Globe,
  backup: Archive,
  external: Cloud,
};

const TECH: Record<string, { label: string; color: string; bg: string; border: string }> = {
  supabase: { label: 'Supabase', color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30' },
  apphost: { label: 'AppHost', color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30' },
  dbhost: { label: 'DBHost', color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/30' },
  npm: { label: 'Proxy', color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30' },
  dns: { label: 'DNS', color: 'text-sky-400', bg: 'bg-sky-500/10', border: 'border-sky-500/30' },
  backup: { label: 'Backup', color: 'text-gray-400', bg: 'bg-gray-500/10', border: 'border-gray-500/30' },
  external: { label: 'External', color: 'text-sky-400', bg: 'bg-sky-500/10', border: 'border-sky-500/30' },
};

export function plannedStack(opts: {
  frontend?: string;
  backend?: string;
  publish?: boolean;
  backup?: boolean;
  gitHint?: string;
}): StackService[] {
  const out: StackService[] = [];
  if (opts.backend && opts.backend !== 'none') {
    out.push({
      name: 'backend',
      module: opts.backend === 'supabase' ? 'supabase' : 'dbhost',
      subtitle: opts.backend,
    });
  }
  if (opts.frontend && opts.frontend !== 'none') {
    out.push({
      name: 'frontend',
      module: 'apphost',
      subtitle: [opts.frontend, opts.gitHint].filter(Boolean).join(' · '),
    });
  }
  if (opts.publish) out.push({ name: 'proxy', module: 'npm', subtitle: 'public URL' });
  if (opts.backup) out.push({ name: 'backup', module: 'backup' });
  return out;
}

export function stackFromWizardSpec(spec: {
  services?: Record<string, unknown>;
} | null | undefined): StackService[] {
  if (!spec?.services) return [];
  return Object.entries(spec.services).map(([name, raw]) => {
    const s = (raw ?? {}) as Record<string, unknown>;
    const module = String(s.module ?? 'apphost');
    const bits: string[] = [];
    if (typeof s.type === 'string') bits.push(s.type);
    if (typeof s.engine === 'string') bits.push(s.engine);
    if (typeof s.git_repo === 'string') {
      bits.push(s.git_repo.split('/').pop()?.replace(/\.git$/i, '') ?? '');
    }
    if (typeof s.domain === 'string') bits.push(s.domain);
    return {
      name,
      module,
      subtitle: bits.filter(Boolean).join(' · ') || undefined,
      node: typeof s.target_node === 'string' ? s.target_node : null,
    };
  });
}

/** Rozsvítí karty podle fáze orchestrator.project_create. */
export function applyOperationPhase(
  services: StackService[],
  phase: string | undefined,
  opStatus: string | undefined,
): StackService[] {
  if (!services.length) return services;
  if (opStatus === 'completed') return services.map((s) => ({ ...s, status: 'done' }));
  const runningOf = (pred: (s: StackService) => boolean): StackService[] =>
    services.map((s) => ({
      ...s,
      status: pred(s) ? 'running' : s.status === 'done' ? 'done' : 'pending',
    }));
  let next: StackService[];
  switch (phase) {
    case 'preflight':
    case 'template':
      next = services.map((s, i) => ({ ...s, status: i === 0 ? 'running' : 'pending' }));
      break;
    case 'project-spec':
    case 'define':
      next = runningOf(() => true);
      break;
    case 'apply':
    case 'consumer-binding':
      next = runningOf((s) => s.module === 'apphost' || s.module === 'supabase' || s.module === 'dbhost');
      break;
    case 'publication':
      next = services.map((s) => ({
        ...s,
        status: s.module === 'npm' ? 'running' : 'done',
      }));
      break;
    case 'verification':
      next = services.map((s) => ({ ...s, status: 'done' }));
      break;
    default:
      next = runningOf(() => true);
  }
  if (opStatus === 'failed') {
    return next.map((s) => ({ ...s, status: s.status === 'running' ? 'error' : s.status }));
  }
  return next;
}

export function StackFlowGraphic({
  services,
  title,
  chainOnly = false,
}: {
  services: StackService[];
  title?: string;
  /**
   * Jen schéma toku, bez seznamu služeb pod ním.
   *
   * Seznam má smysl, když nese stav (co běží, co je hotové). V náhledu před
   * stavbou žádný stav není a je to totéž dvakrát — Filip 2026-09-09 nad
   * průvodcem importem: „tohle nikdo nechápe."
   */
  chainOnly?: boolean;
}) {
  if (!services.length) return null;
  const byMod = (m: string) => services.find((s) => s.module === m);
  const chain: Array<{ kind: 'internet' } | { kind: 'svc'; svc: StackService; label: string }> = [];
  const npm = byMod('npm');
  const fe = byMod('apphost');
  const be = services.find((s) => s.module === 'supabase' || s.module === 'dbhost' || s.module === 'external');
  if (npm) {
    chain.push({ kind: 'internet' });
    chain.push({ kind: 'svc', svc: npm, label: 'proxy' });
  }
  if (fe) chain.push({ kind: 'svc', svc: fe, label: 'frontend' });
  if (be) chain.push({ kind: 'svc', svc: be, label: 'backend' });

  return (
    <div className='space-y-3'>
      {title && (
        <div className='text-[9px] text-gray-600 uppercase tracking-wider font-medium'>{title}</div>
      )}
      {chain.length >= 2 && (
        <div className='flex items-center gap-2 flex-wrap'>
          {chain.map((step, i) => {
            const last = i === chain.length - 1;
            if (step.kind === 'internet') {
              return (
                <div key={`inet-${i}`} className='flex items-center gap-2'>
                  <div className='flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-700 bg-gray-900/60'>
                    <Globe size={14} className='text-blue-400' />
                    <span className='text-xs text-gray-300 font-medium'>Internet</span>
                  </div>
                  {!last && <span className='text-gray-600 text-lg'>→</span>}
                </div>
              );
            }
            const tech = TECH[step.svc.module] ?? TECH.apphost;
            const Icon = ICONS[step.svc.module] ?? Server;
            const ok = step.svc.status === 'done';
            return (
              <div key={`${step.svc.name}-${i}`} className='flex items-center gap-2'>
                <div
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 rounded-lg border-2',
                    ok ? `${tech.border} ${tech.bg}` : 'border-gray-700 bg-gray-900/40',
                  )}
                >
                  <Icon size={14} className={ok ? tech.color : 'text-gray-500'} />
                  <div className='flex flex-col leading-tight'>
                    <span className='text-xs text-white font-bold'>{step.svc.name}</span>
                    {step.svc.subtitle && (
                      <span className='text-[9px] text-gray-500 font-mono'>{step.svc.subtitle}</span>
                    )}
                  </div>
                  <span className='text-[9px] text-gray-500 uppercase ml-1'>{step.label}</span>
                </div>
                {!last && <span className='text-gray-600 text-lg'>→</span>}
              </div>
            );
          })}
        </div>
      )}

      {!chainOnly && (
      <div className='space-y-2'>
        {services.map((svc) => {
          const tech = TECH[svc.module] ?? TECH.apphost;
          const Icon = ICONS[svc.module] ?? Server;
          const st = svc.status ?? 'pending';
          return (
            <div
              key={svc.name}
              className={cn(
                'flex items-center gap-3 p-3.5 rounded-xl border transition-all',
                st === 'done' && 'border-green-500/20 bg-green-500/5',
                st === 'error' && 'border-red-500/20 bg-red-500/5',
                st === 'running' && 'border-blue-500/20 bg-blue-500/5',
                st === 'pending' && 'border-gray-800 bg-gray-900/20',
              )}
            >
              <div
                className={cn(
                  'w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0',
                  st === 'done' && 'bg-green-500/10',
                  st === 'error' && 'bg-red-500/10',
                  st === 'running' && 'bg-blue-500/10',
                  st === 'pending' && 'bg-gray-800/50',
                )}
              >
                {st === 'running'
                  ? <Loader2 size={18} className='text-blue-400 animate-spin' />
                  : st === 'done'
                  ? <CheckCircle2 size={18} className='text-green-400' />
                  : st === 'error'
                  ? <XCircle size={18} className='text-red-400' />
                  : <Icon size={18} className={tech.color} />}
              </div>
              <div className='flex-1 min-w-0'>
                <div className='flex items-center gap-2'>
                  <span className='text-sm font-semibold text-white'>{svc.name}</span>
                  <span className={cn('text-[10px] px-1.5 py-0.5 rounded font-medium', tech.color, tech.bg)}>
                    {tech.label}
                  </span>
                  {svc.node && (
                    <span className='text-[10px] font-mono text-violet-300'>@{svc.node}</span>
                  )}
                </div>
                {svc.subtitle && (
                  <div className='text-[11px] text-gray-500 truncate'>{svc.subtitle}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}
